/**
 * FINANCIAL CALCULATIONS UTILITIES
 *
 * This module provides proper financial calculation functions for real estate investment metrics.
 * All calculations are based on actual business logic and user inputs, not random values.
 *
 * IMPORTANT: These calculations represent significant financial projections and should be
 * validated by financial experts before use in production environments.
 */

export interface FinancialCalculationInputs {
  totalTokenValue: number; // Total value of all tokens in base currency
  assetYield: number; // Annual yield percentage (0-100)
  investmentReturn: number; // Expected investment return percentage (0-100)
  holdingPeriod?: number; // Optional holding period in years (default: 1)
}

/**
 * Calculate total rental income based on asset yield
 *
 * Formula: Total Rental Income = Total Token Value × (Asset Yield / 100)
 *
 * @param inputs Financial calculation inputs
 * @returns Total annual rental income
 */
export function calculateTotalRentalIncome(inputs: FinancialCalculationInputs): number {
  const { totalTokenValue, assetYield } = inputs;

  if (totalTokenValue <= 0 || assetYield < 0 || assetYield > 100) {
    return 0;
  }

  const rentalIncome = totalTokenValue * (assetYield / 100);
  return Math.round(rentalIncome * 100) / 100; // Round to 2 decimal places
}

/**
 * Calculate total asset appreciation based on investment return
 *
 * Formula: Total Asset Appreciation = Total Token Value × (Investment Return / 100)
 *
 * @param inputs Financial calculation inputs
 * @returns Total expected appreciation
 */
export function calculateTotalAssetAppreciation(inputs: FinancialCalculationInputs): number {
  const { totalTokenValue, investmentReturn } = inputs;

  if (totalTokenValue <= 0 || investmentReturn < 0 || investmentReturn > 1000) {
    // Allow up to 1000% for high-growth scenarios
    return 0;
  }

  const appreciation = totalTokenValue * (investmentReturn / 100);
  return Math.round(appreciation * 100) / 100; // Round to 2 decimal places
}

/**
 * Calculate compound annual growth rate (CAGR) for investment projections
 *
 * Formula: CAGR = (Ending Value / Beginning Value)^(1/Time Period) - 1
 *
 * @param beginningValue Initial investment value
 * @param endingValue Final investment value
 * @param timePeriod Time period in years
 * @returns CAGR as percentage
 */
export function calculateCAGR(
  beginningValue: number,
  endingValue: number,
  timePeriod: number
): number {
  if (beginningValue <= 0 || endingValue <= 0 || timePeriod <= 0) {
    return 0;
  }

  const cagr = Math.pow(endingValue / beginningValue, 1 / timePeriod) - 1;
  return Math.round(cagr * 10000) / 100; // Return as percentage with 2 decimal places
}

/**
 * Calculate total return including both rental income and appreciation
 *
 * Formula: Total Return = Rental Income + Asset Appreciation
 *
 * @param inputs Financial calculation inputs
 * @returns Total expected return
 */
export function calculateTotalReturn(inputs: FinancialCalculationInputs): number {
  const rentalIncome = calculateTotalRentalIncome(inputs);
  const appreciation = calculateTotalAssetAppreciation(inputs);
  return rentalIncome + appreciation;
}

/**
 * Validate financial calculation inputs
 *
 * @param inputs Financial calculation inputs
 * @returns Validation result with errors array
 */
export function validateFinancialInputs(inputs: FinancialCalculationInputs): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (inputs.totalTokenValue <= 0) {
    errors.push('Total token value must be greater than 0');
  }

  if (inputs.assetYield < 0 || inputs.assetYield > 100) {
    errors.push('Asset yield must be between 0% and 100%');
  }

  if (inputs.investmentReturn < 0 || inputs.investmentReturn > 1000) {
    errors.push('Investment return must be between 0% and 1000%');
  }

  if (inputs.holdingPeriod && inputs.holdingPeriod <= 0) {
    errors.push('Holding period must be greater than 0 years');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Calculate expected yield metrics for display purposes
 *
 * @param inputs Financial calculation inputs
 * @returns Formatted financial metrics
 */
export function getFinancialMetrics(inputs: FinancialCalculationInputs) {
  const validation = validateFinancialInputs(inputs);

  if (!validation.isValid) {
    return {
      totalRentalIncome: 0,
      totalAssetAppreciation: 0,
      totalReturn: 0,
      annualYieldPercentage: inputs.assetYield,
      investmentReturnPercentage: inputs.investmentReturn,
      errors: validation.errors,
    };
  }

  const totalRentalIncome = calculateTotalRentalIncome(inputs);
  const totalAssetAppreciation = calculateTotalAssetAppreciation(inputs);
  const totalReturn = calculateTotalReturn(inputs);

  return {
    totalRentalIncome,
    totalAssetAppreciation,
    totalReturn,
    annualYieldPercentage: inputs.assetYield,
    investmentReturnPercentage: inputs.investmentReturn,
    errors: [],
  };
}
